import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CaretLeft,
  Eye,
  Info,
  Plus,
  SlidersHorizontal,
  X,
} from '@phosphor-icons/react'
import { Button } from '../ui/foundation'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../ui/dialog'
import { useAuth } from '../../hooks/useAuth'
import { useAnalytics } from '../../hooks/useAnalytics'
import { useAuthToken } from '../../hooks/useAuthToken'
import { useMobileDetection } from '../../hooks/useMobileDetection'
import { StorageService, type BoardData } from '../../services/StorageService'
import { signUserImages, uploadUserImage } from '../../services/userImages'
import { getShippingPolicyBrief } from '../../constants/shipping'
import { trackAddToCartConversion } from '../../utils/googleAds'
import { serializeForCheckout } from '../../utils/adAttribution'
import { logError, logInfo } from '../../utils/logger'
import {
  clearBoardThumbnailPreview,
  publishBoardThumbnailPreview,
} from '../../utils/boardThumbnailPreview'
import { uploadBoardThumbnail } from '../../utils/supabaseStorage'
import type { PrintSize } from '../../constants/print'
import { PRINT_PRICING_CENTS } from '../../constants/printPricing'
import {
  composeMobileImages,
  createEmptyMobileCanvasState,
  getNextComposePreset,
  getNextCornerStyle,
  getNextSpacingStyle,
  serializeMobileCanvasState,
  toMobileCanvasState,
  type MobileBoardImage,
  type MobileCanvasState,
} from './mobileBoard'
import { renderMobileBoardThumbnailBlob } from './mobileBoardThumbnail'
import { MobileBoardPreview } from './MobileBoardPreview'

const storage = new StorageService()
const sizeOrder: PrintSize[] = ['small', 'medium', 'large']

const DEFAULT_UNTITLED_BOARD_TITLE = 'Untitled board'
const MOBILE_BOARD_RADIUS_CLASS = 'rounded-[16px]'
const USD_WHOLE_NUMBER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const formatUSDWholeFromCents = (cents: number): string =>
  USD_WHOLE_NUMBER.format(cents / 100)

const MOBILE_SIZE_OPTIONS: Record<
  PrintSize,
  {
    name: string
    dimensions: string
    summary: string
  }
> = {
  small: {
    name: 'Intimo',
    dimensions: '8 × 12"',
    summary: 'For desk, shelf, or smaller wall moments',
  },
  medium: {
    name: 'Classico',
    dimensions: '16 × 24"',
    summary: 'For the wall you pass every day',
  },
  large: {
    name: 'Grande',
    dimensions: '24 × 36"',
    summary: 'For an open wall with more presence',
  },
}

const isUntitledBoardTitle = (value?: string | null) => {
  const title = value?.trim()
  return !title || title === 'New Board'
}

const getEditableBoardTitle = (board?: BoardData | null) => {
  const title = board?.title?.trim() ?? ''
  return isUntitledBoardTitle(title) ? '' : title
}

const getBoardTitleLabel = (board?: BoardData | null) => {
  const title = board?.title?.trim()
  return isUntitledBoardTitle(title) ? DEFAULT_UNTITLED_BOARD_TITLE : title
}

const isLandscapePhoneViewport = (
  deviceType: 'mobile' | 'tablet' | 'desktop'
) =>
  deviceType === 'mobile' &&
  typeof window !== 'undefined' &&
  window.innerWidth > window.innerHeight

const refreshSignedUrls = async (state: MobileCanvasState) => {
  const storagePaths = state.elements
    .map((element) => element.storagePath)
    .filter((value): value is string => !!value)

  if (storagePaths.length === 0) return state

  const result = await signUserImages([...new Set(storagePaths)])
  if (!result.success || !result.data) return state

  return {
    ...state,
    elements: state.elements.map((element) => {
      if (!element.storagePath) return element
      const nextSrc = result.data?.[element.storagePath]
      return nextSrc ? { ...element, src: nextSrc } : element
    }),
  }
}

const MOBILE_IMAGE_REVEAL_TIMEOUT_MS = 900

const preloadBoardImages = async (
  state: MobileCanvasState,
  timeoutMs = MOBILE_IMAGE_REVEAL_TIMEOUT_MS
) => {
  const srcs = [
    ...new Set(state.elements.map((element) => element.src).filter(Boolean)),
  ]

  if (
    srcs.length === 0 ||
    typeof window === 'undefined' ||
    typeof Image === 'undefined'
  ) {
    return
  }

  const loadImage = (src: string) =>
    new Promise<void>((resolve) => {
      const image = new Image()

      image.onload = () => resolve()
      image.onerror = () => resolve()

      image.src = src

      if (typeof image.decode === 'function') {
        void image.decode().then(resolve).catch(resolve)
      }
    })

  const timeout = new Promise<void>((resolve) => {
    window.setTimeout(resolve, timeoutMs)
  })

  await Promise.race([
    Promise.allSettled(srcs.map((src) => loadImage(src))).then(() => undefined),
    timeout,
  ])
}

interface ImageManagerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  images: MobileBoardImage[]
  onUpload: () => void
  onRemove: (imageId: string) => void
  onConfirm: () => void
  uploading: boolean
  composing: boolean
}

const ImageManagerDialog: React.FC<ImageManagerDialogProps> = ({
  open,
  onOpenChange,
  images,
  onUpload,
  onRemove,
  onConfirm,
  uploading,
  composing,
}) => {
  const isBusy = uploading || composing
  const description = uploading
    ? 'Uploading your photos now.'
    : composing
      ? 'Making your board now.'
      : images.length === 0
        ? "Add your photos here, then tap Make board. We'll arrange everything for you."
        : "Add up to 20 of your favorite images and we'll make print-ready board for you."

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="sm"
        className="max-h-[min(100svh-2rem,42rem)] grid-rows-[auto_auto_auto_minmax(0,1fr)_auto] gap-5"
        showCloseButton={!isBusy}
        onInteractOutside={(event) => {
          if (isBusy) event.preventDefault()
        }}
        onEscapeKeyDown={(event) => {
          if (isBusy) event.preventDefault()
        }}
      >
        <DialogTitle>Manage images</DialogTitle>
        <DialogDescription>{description}</DialogDescription>

        <Button
          variant="secondary"
          size="md"
          className="w-full justify-center bg-white"
          onClick={onUpload}
          loading={uploading}
          disabled={composing}
        >
          <Plus className="h-5 w-5" />
          Add image
        </Button>

        {images.length === 0 ? (
          <div className="rounded-[20px] border border-stone-200 bg-stone-50 px-4 py-5 text-center font-sans text-fs-300 text-ink-500">
            No images yet.
          </div>
        ) : (
          <div className="min-h-0 overflow-y-auto pr-1">
            <div className="grid auto-rows-max grid-cols-2 items-start gap-3">
              {images.map((image) => (
                <div
                  key={image.id}
                  className="group relative aspect-[4/5] min-w-0 overflow-hidden rounded-[20px] bg-background-100"
                >
                  <img
                    src={image.src}
                    alt="Selected board image"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => onRemove(image.id)}
                    disabled={isBusy}
                    className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(36,34,30,0.08)] bg-background-50/96 text-ink-600 shadow-[0_8px_24px_rgba(36,34,30,0.12)] transition-opacity duration-settle hover:text-ink-700 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 md:h-8 md:w-8 md:opacity-0 md:shadow-none md:group-hover:opacity-100 md:focus:opacity-100"
                    aria-label="Remove image"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button
          variant="primary"
          size="md"
          className="w-full justify-center"
          onClick={onConfirm}
          loading={composing}
          disabled={images.length === 0 || uploading}
        >
          {composing ? 'Making board...' : 'Make board'}
        </Button>
      </DialogContent>
    </Dialog>
  )
}

interface FullscreenPreviewProps {
  open: boolean
  onClose: () => void
  state: MobileCanvasState
}

const FullscreenPreview: React.FC<FullscreenPreviewProps> = ({
  open,
  onClose,
  state,
}) => {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-[1400] bg-[rgba(24,23,20,0.72)] backdrop-blur-[6px]">
      <div className="flex h-full flex-col px-4 pb-6 pt-4">
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/16 hover:text-white"
            aria-label="Close fullscreen preview"
          >
            <X size={18} weight="regular" />
          </button>
        </div>

        <div className="flex flex-1 items-center justify-center">
          <div className="w-full drop-shadow-[0_32px_80px_rgba(0,0,0,0.38)]">
            <MobileBoardPreview state={state} />
          </div>
        </div>
      </div>
    </div>
  )
}

interface RefineSheetProps {
  open: boolean
  onClose: () => void
  onLayout: () => void
  onCorners: () => void
  onSpacing: () => void
  composePreset: number
  cornerStyle: string
  spacingStyle: string
  disabled: boolean
}

const RefineSheet: React.FC<RefineSheetProps> = ({
  open,
  onClose,
  onLayout,
  onCorners,
  onSpacing,
  composePreset,
  cornerStyle,
  spacingStyle,
  disabled,
}) => {
  if (!open) return null

  const rowClass =
    'flex w-full items-center justify-between py-4 font-sans text-fs-300 text-ink-700 transition-colors duration-settle ease-precise disabled:text-ink-300 active:opacity-70'
  const activeIndicatorClass =
    'ml-auto font-sans text-fs-200 uppercase tracking-kicker text-ink-400'

  return (
    <div
      className="fixed inset-0 z-[1200]"
      onClick={onClose}
      aria-label="Close refine panel"
    >
      <div
        className="absolute inset-x-0 bottom-0 rounded-t-[28px] border-t border-[rgba(36,34,30,0.06)] bg-white px-6 pb-10 pt-5 shadow-[0_-24px_60px_rgba(61,59,54,0.10)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <p className="font-serif text-fs-600 italic text-ink-700">Refine</p>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center text-ink-400 transition-colors duration-150 hover:text-ink-700"
            aria-label="Close"
          >
            <X size={16} weight="regular" />
          </button>
        </div>

        <div className="divide-y divide-[rgba(36,34,30,0.06)]">
          <button
            type="button"
            onClick={onLayout}
            disabled={disabled}
            className={rowClass}
          >
            <span>Layout</span>
            {composePreset !== 0 && (
              <span className={activeIndicatorClass}>Changed</span>
            )}
          </button>

          <button
            type="button"
            onClick={onCorners}
            disabled={disabled}
            className={rowClass}
          >
            <span>Corners</span>
            {cornerStyle !== 'small' && (
              <span className={activeIndicatorClass}>Changed</span>
            )}
          </button>

          <button
            type="button"
            onClick={onSpacing}
            disabled={disabled}
            className={rowClass}
          >
            <span>Spacing</span>
            {spacingStyle !== 'light' && (
              <span className={activeIndicatorClass}>Changed</span>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export const MobileBoardFlow: React.FC = () => {
  const { boardId } = useParams<{ boardId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const analytics = useAnalytics()
  const { getAuthToken } = useAuthToken()
  const { deviceType } = useMobileDetection()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const saveTimeoutRef = useRef<number | null>(null)
  const lastPersistedStateRef = useRef<string>('')

  const [board, setBoard] = useState<BoardData | null>(null)
  const [state, setState] = useState<MobileCanvasState>(
    createEmptyMobileCanvasState()
  )
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [imagesOpen, setImagesOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [printOpen, setPrintOpen] = useState(false)
  const [selectedSize, setSelectedSize] = useState<PrintSize>('medium')
  const [error, setError] = useState<string | null>(null)
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const [isComposingBoard, setIsComposingBoard] = useState(false)
  const [hasCreatedBoardLayout, setHasCreatedBoardLayout] = useState(false)
  const [isTweaking, setIsTweaking] = useState(false)
  const [isRenamingTitle, setIsRenamingTitle] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')
  const [refineOpen, setRefineOpen] = useState(false)
  const [isContentVisible, setIsContentVisible] = useState(false)

  const isLandscapeViewOnly = isLandscapePhoneViewport(deviceType)

  const persistBoard = useCallback(
    async (nextBoard: BoardData, nextState: MobileCanvasState) => {
      if (!user) return

      const canvasState = serializeMobileCanvasState(nextState)
      lastPersistedStateRef.current = canvasState
      const result = await storage.saveBoard({
        ...nextBoard,
        id: nextBoard.id,
        userId: user.id,
        templateType: 'blank',
        canvasState,
        thumbnail: nextBoard.thumbnail,
      })
      if (!result.success || !result.boardId) return

      const nextBoardId = result.boardId
      if (nextState.elements.length === 0) {
        clearBoardThumbnailPreview({
          userId: user.id,
          boardId: nextBoardId,
          persistedThumbnailUrl: null,
        })

        const clearResult = await storage.updateBoardThumbnail(
          nextBoardId,
          null
        )
        if (!clearResult.success) {
          logError('MobileBoardFlow', 'Failed to clear board thumbnail URL', {
            boardId: nextBoardId,
            error: clearResult.error,
          })
        }

        setBoard((previous) => ({
          ...(previous || nextBoard),
          ...nextBoard,
          id: nextBoardId,
          userId: user.id,
          canvasState,
          thumbnail: undefined,
          lastSaved: result.lastSaved,
        }))
        return
      }

      let persistedThumbnailUrl = nextBoard.thumbnail
      try {
        const thumbnailBlob = await renderMobileBoardThumbnailBlob(nextState)
        await publishBoardThumbnailPreview({
          userId: user.id,
          boardId: nextBoardId,
          blob: thumbnailBlob,
        })

        const uploadedThumbnail = await uploadBoardThumbnail(
          user.id,
          nextBoardId,
          thumbnailBlob
        )

        if (uploadedThumbnail) {
          const thumbnailSaveResult = await storage.updateBoardThumbnail(
            nextBoardId,
            uploadedThumbnail
          )

          if (thumbnailSaveResult.success) {
            persistedThumbnailUrl = uploadedThumbnail
            clearBoardThumbnailPreview({
              userId: user.id,
              boardId: nextBoardId,
              persistedThumbnailUrl: uploadedThumbnail,
            })
          } else {
            logError(
              'MobileBoardFlow',
              'Failed to persist board thumbnail URL',
              {
                boardId: nextBoardId,
                error: thumbnailSaveResult.error,
              }
            )
          }
        }
      } catch (thumbnailError) {
        logError(
          'MobileBoardFlow',
          'Failed to refresh mobile board thumbnail',
          {
            error: thumbnailError,
            boardId: nextBoardId,
          }
        )
      }

      setBoard((previous) => ({
        ...(previous || nextBoard),
        ...nextBoard,
        id: nextBoardId,
        userId: user.id,
        canvasState,
        thumbnail: persistedThumbnailUrl,
        lastSaved: result.lastSaved,
      }))
    },
    [user]
  )

  useEffect(() => {
    if (!boardId || !user) return

    let cancelled = false

    const load = async () => {
      setLoading(true)
      setIsContentVisible(false)
      setError(null)
      const result = await storage.loadBoard(boardId)
      if (cancelled) return

      if (!result.success || !result.board) {
        setError(result.error || 'Board not found')
        setLoading(false)
        return
      }

      const nextBoard = result.board
      const mobileState = await refreshSignedUrls(
        toMobileCanvasState(nextBoard.canvasState)
      )
      await preloadBoardImages(mobileState)
      lastPersistedStateRef.current = serializeMobileCanvasState(mobileState)

      setBoard(nextBoard)
      setState(mobileState)
      setHasCreatedBoardLayout(mobileState.elements.length > 0)
      setLoading(false)
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [boardId, user])

  useEffect(() => {
    if (loading || !board || error === 'Board not found') return

    const revealFrame = window.requestAnimationFrame(() => {
      setIsContentVisible(true)
    })

    return () => {
      window.cancelAnimationFrame(revealFrame)
    }
  }, [loading, board, error])

  useEffect(() => {
    if (!board || !user || loading) return

    const serializedState = serializeMobileCanvasState(state)
    if (lastPersistedStateRef.current === serializedState) return

    if (saveTimeoutRef.current) {
      window.clearTimeout(saveTimeoutRef.current)
    }

    saveTimeoutRef.current = window.setTimeout(() => {
      void persistBoard(board, state)
    }, 500)

    return () => {
      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [board, state, user, loading, persistBoard])

  const updateState = (
    updater: (current: MobileCanvasState) => MobileCanvasState
  ) => {
    setState((current) => updater(current))
  }

  const recomputeBoard = useCallback(() => {
    updateState((current) => ({
      ...current,
      elements: composeMobileImages(current.elements, {
        composePreset: current.composePreset,
        spacingStyle: current.spacingStyle,
        width: current.width,
        height: current.height,
      }),
    }))
  }, [])

  const startTitleRename = () => {
    setDraftTitle(getEditableBoardTitle(board))
    setIsRenamingTitle(true)
  }

  const commitTitleRename = async () => {
    const trimmed = draftTitle.trim()
    setIsRenamingTitle(false)
    if (!board || trimmed === getEditableBoardTitle(board)) return
    const updatedBoard = { ...board, title: trimmed }
    setBoard(updatedBoard)
    await persistBoard(updatedBoard, state)
  }

  const cancelTitleRename = () => {
    setIsRenamingTitle(false)
    setDraftTitle('')
  }

  const handleConfirmBoard = useCallback(async () => {
    if (uploading || isComposingBoard || isLandscapeViewOnly) return
    if (state.elements.length === 0) return

    setIsComposingBoard(true)

    recomputeBoard()

    await new Promise<void>((resolve) => {
      window.setTimeout(() => resolve(), 320)
    })

    setHasCreatedBoardLayout(true)
    setImagesOpen(false)
    setIsComposingBoard(false)
  }, [
    uploading,
    isComposingBoard,
    isLandscapeViewOnly,
    state.elements.length,
    recomputeBoard,
  ])

  const openPicker = () => {
    if (isLandscapeViewOnly || uploading) return
    fileInputRef.current?.click()
  }

  const triggerTweakAnimation = () => {
    setIsTweaking(true)
    window.setTimeout(() => setIsTweaking(false), 150)
  }

  const handleCompose = () => {
    triggerTweakAnimation()
    updateState((current) => {
      const composePreset = getNextComposePreset(current.composePreset)
      return {
        ...current,
        composePreset,
        elements: composeMobileImages(current.elements, {
          composePreset,
          spacingStyle: current.spacingStyle,
          width: current.width,
          height: current.height,
        }),
      }
    })
  }

  const handleShape = () => {
    triggerTweakAnimation()
    updateState((current) => ({
      ...current,
      cornerStyle: getNextCornerStyle(current.cornerStyle),
    }))
  }

  const handleSpace = () => {
    triggerTweakAnimation()
    updateState((current) => {
      const spacingStyle = getNextSpacingStyle(current.spacingStyle)
      return {
        ...current,
        spacingStyle,
        elements: composeMobileImages(current.elements, {
          composePreset: current.composePreset,
          spacingStyle,
          width: current.width,
          height: current.height,
        }),
      }
    })
  }

  const handleRemoveImage = (imageId: string) => {
    if (isLandscapeViewOnly) return

    updateState((current) => {
      const remaining = current.elements.filter(
        (element) => element.id !== imageId
      )
      return {
        ...current,
        elements: composeMobileImages(remaining, {
          composePreset: current.composePreset,
          spacingStyle: current.spacingStyle,
          width: current.width,
          height: current.height,
        }),
      }
    })
  }

  const handleFilesSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!user || files.length === 0 || isLandscapeViewOnly) return

    setUploading(true)
    setError(null)
    setImagesOpen(true)

    const uploadedElements: MobileBoardImage[] = []

    for (const file of files.slice(0, 20)) {
      const result = await uploadUserImage(file)
      if (!result.success || !result.data) {
        setError(result.error || 'Failed to upload image')
        continue
      }

      uploadedElements.push({
        id: `mobile-image-${crypto.randomUUID()}`,
        type: 'image',
        src: result.data.signedUrl,
        storagePath: result.data.storagePath,
        width: result.data.width || 320,
        height: result.data.height || 320,
        left: 0,
        top: 0,
        scaleX: 1,
        scaleY: 1,
        originalWidth: result.data.width || 320,
        originalHeight: result.data.height || 320,
        metadata: { source: 'upload' },
      })
    }

    if (uploadedElements.length > 0) {
      updateState((current) => ({
        ...current,
        elements: [...current.elements, ...uploadedElements],
      }))
      setImagesOpen(true)
    }

    setUploading(false)
  }

  const handleBuy = async () => {
    if (!user || !board?.id || isCheckingOut || state.elements.length === 0) {
      return
    }

    setIsCheckingOut(true)
    setError(null)

    const subtotal = PRINT_PRICING_CENTS[selectedSize]
    analytics.purchaseStarted(subtotal / 100, selectedSize)

    try {
      logInfo('MobileBoardFlow', 'Creating checkout', {
        size: selectedSize,
        userId: user.id,
        visionBoardId: board.id,
      })

      const token = await getAuthToken()
      if (!token) {
        throw new Error('You must be logged in to checkout.')
      }
      const attribution = serializeForCheckout()

      const response = await fetch('/.netlify/functions/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          printSize: selectedSize,
          quantity: 1,
          userId: user.id,
          visionBoardId: board.id,
          attribution,
        }),
      })

      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.checkoutUrl) {
        throw new Error(result.error || 'Failed to create checkout.')
      }

      logInfo('MobileBoardFlow', 'Redirecting to Shopify checkout', {
        checkoutUrl: result.checkoutUrl,
      })

      window.location.href = result.checkoutUrl as string
    } catch (checkoutError) {
      const errorMessage =
        checkoutError instanceof Error
          ? checkoutError.message
          : 'Failed to create checkout.'
      analytics.purchaseError('checkout_creation', errorMessage)
      logError('MobileBoardFlow', 'Checkout failed', { error: checkoutError })
      setError(errorMessage)
      setIsCheckingOut(false)
    }
  }

  const handleOpenPrint = () => {
    if (!board?.id || state.elements.length === 0) {
      return
    }

    analytics.createComplete(board.id, state.elements.length)
    trackAddToCartConversion('canvas')
    setPrintOpen(true)
  }

  if (loading) {
    return <main className="min-h-screen bg-background-50" aria-hidden="true" />
  }

  if (!board || error === 'Board not found') {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-stone-100 px-6 text-center">
        <p className="font-serif text-fs-600 italic text-ink-700">
          This board is no longer available.
        </p>
        <Button variant="primary" size="md" onClick={() => navigate('/boards')}>
          Back to boards
        </Button>
      </main>
    )
  }

  const hasImages = state.elements.length > 0
  const titleLabel = getBoardTitleLabel(board)
  const showPreviewAction = hasImages
  const printCtaLabel = 'Order print'

  return (
    <>
      <main
        className={[
          'flex h-[100dvh] flex-col bg-background-50 px-4 pt-4 transition-opacity duration-settle ease-precise',
          isContentVisible ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFilesSelected}
        />

        <div className="mx-auto flex w-full max-w-md flex-col gap-4">
          <header className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate('/boards')}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center text-ink-400 transition-colors duration-150 hover:text-ink-700"
              aria-label="Back to boards"
            >
              <CaretLeft size={16} weight="regular" aria-hidden="true" />
            </button>

            <div className="flex flex-col items-center text-center">
              {isRenamingTitle ? (
                <input
                  autoFocus
                  maxLength={60}
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onBlur={() => void commitTitleRename()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void commitTitleRename()
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault()
                      cancelTitleRename()
                    }
                  }}
                  placeholder={DEFAULT_UNTITLED_BOARD_TITLE}
                  className="w-[220px] border-b border-[rgba(36,34,30,0.12)] bg-transparent px-2 pb-1 text-center font-serif text-fs-600 text-ink-700 outline-none placeholder:text-ink-400"
                />
              ) : (
                <button
                  type="button"
                  onClick={startTitleRename}
                  className={[
                    'max-w-[240px] truncate transition-colors duration-150',
                    isUntitledBoardTitle(board.title)
                      ? 'font-serif text-fs-600 italic text-ink-400 hover:text-ink-500'
                      : 'font-serif text-fs-600 font-semibold text-ink-700 hover:text-ink-600',
                  ].join(' ')}
                >
                  {titleLabel}
                </button>
              )}
              <p className="font-sans text-fs-200 text-ink-400">
                {uploading ? 'Uploading...' : ' '}
              </p>
            </div>

            {showPreviewAction ? (
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center text-ink-400 transition-colors duration-150 hover:text-ink-700"
                aria-label="Open fullscreen preview"
              >
                <Eye size={16} weight="regular" aria-hidden="true" />
              </button>
            ) : (
              <div className="h-11 w-11 shrink-0" aria-hidden="true" />
            )}
          </header>

          {isLandscapeViewOnly ? (
            <div className="rounded-[18px] border border-stone-200 bg-white/90 px-4 py-3 text-center font-sans text-fs-300 text-ink-500">
              Landscape phone mode is preview-only. Rotate back to portrait to
              edit or print.
            </div>
          ) : null}
        </div>

        <div
          className={[
            'mx-auto flex w-full max-w-md flex-1 flex-col',
            hasImages
              ? 'justify-start gap-2 pb-36 pt-2'
              : 'justify-center gap-4 pb-16 pt-0',
          ].join(' ')}
        >
          <div className="w-full transition-all duration-settle ease-precise">
            <div
              className={[
                'relative transition-all duration-settle ease-precise',
                isComposingBoard
                  ? 'scale-[0.98] opacity-85'
                  : isTweaking
                    ? 'scale-[0.985] opacity-95'
                    : 'scale-100 opacity-100',
              ].join(' ')}
            >
              {!hasImages && !isLandscapeViewOnly ? (
                <button
                  type="button"
                  onClick={() => setImagesOpen(true)}
                  disabled={isComposingBoard}
                  className="w-full text-left"
                  aria-label="Add photos to your board"
                >
                  <div
                    className={`relative aspect-[4/3] w-full overflow-hidden border border-[rgba(61,59,54,0.08)] ${MOBILE_BOARD_RADIUS_CLASS}`}
                    style={{ boxShadow: '0 24px 56px rgba(61,59,54,0.12)' }}
                  >
                    <div className="grain grain-light absolute inset-0 bg-canvas-empty" />
                    <div className="relative flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[rgba(36,34,30,0.12)] bg-background-50/70">
                        <Plus className="h-5 w-5 text-ink-500" />
                      </div>
                      <span className="font-serif text-fs-600 italic text-ink-500">
                        Tap to add your photos.
                      </span>
                    </div>
                  </div>
                </button>
              ) : (
                <MobileBoardPreview state={state} />
              )}

              {isComposingBoard ? (
                <div
                  className={`absolute inset-0 flex items-center justify-center bg-[rgba(248,245,238,0.82)] backdrop-blur-[3px] ${MOBILE_BOARD_RADIUS_CLASS}`}
                >
                  <div className="rounded-[18px] border border-[rgba(61,59,54,0.08)] bg-white/88 px-4 py-3 text-center shadow-[0_18px_40px_rgba(61,59,54,0.08)]">
                    <p className="font-serif text-fs-500 italic text-ink-700">
                      Making your board
                    </p>
                    <p className="mt-1 font-sans text-fs-300 text-ink-500">
                      This should only take a second.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {hasImages ? (
            <div className="flex items-start gap-2 rounded-[16px] bg-white/72 px-3 py-2.5 text-ink-500">
              <Info
                size={14}
                weight="regular"
                className="mt-0.5 shrink-0 text-ink-400"
                aria-hidden="true"
              />
              <p className="font-sans text-fs-300 leading-relaxed">
                For the full editing experience, open your board on your laptop.
              </p>
            </div>
          ) : null}

          {hasImages && (
            <Button
              variant="secondary"
              size="lg"
              className="w-full justify-center rounded-[18px] !bg-white text-ink-700 hover:!bg-white"
              onClick={() => setImagesOpen(true)}
              disabled={isLandscapeViewOnly || isComposingBoard}
            >
              {hasCreatedBoardLayout ? 'Manage images' : 'Add images'}
            </Button>
          )}

          {error ? (
            <div className="rounded-[18px] border border-error-200 bg-white/90 px-4 py-3 font-sans text-fs-300 text-error-500">
              {error}
            </div>
          ) : null}
        </div>

        {hasImages ? (
          <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[1100] px-4 pb-6 pt-3">
            <div className="pointer-events-auto mx-auto flex w-full max-w-md flex-col gap-2">
              <button
                type="button"
                onClick={() => setRefineOpen(true)}
                disabled={isLandscapeViewOnly || state.elements.length === 0}
                className="flex items-center justify-center gap-2 py-2 text-ink-400 transition-colors duration-settle ease-precise hover:text-ink-600 disabled:opacity-30"
              >
                <SlidersHorizontal size={13} weight="regular" />
                <span className="font-sans text-fs-200 uppercase tracking-kicker">
                  Refine
                </span>
              </button>

              <Button
                variant="primary"
                size="lg"
                className="w-full justify-center rounded-[22px]"
                onClick={handleOpenPrint}
                disabled={
                  isLandscapeViewOnly ||
                  state.elements.length === 0 ||
                  uploading
                }
              >
                Order print
              </Button>
            </div>
          </div>
        ) : null}
      </main>

      <ImageManagerDialog
        open={imagesOpen}
        onOpenChange={setImagesOpen}
        images={state.elements}
        onUpload={openPicker}
        onRemove={handleRemoveImage}
        onConfirm={() => void handleConfirmBoard()}
        uploading={uploading}
        composing={isComposingBoard}
      />

      <FullscreenPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        state={state}
      />

      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent size="sm" className="gap-5">
          <DialogTitle>Print your board</DialogTitle>
          <DialogDescription>
            Select a size, then continue to checkout.
          </DialogDescription>

          <button
            type="button"
            onClick={() => {
              setPrintOpen(false)
              setPreviewOpen(true)
            }}
            className="mx-auto w-full overflow-hidden rounded-[20px] border border-stone-200"
            aria-label="View fullscreen preview"
          >
            <MobileBoardPreview state={state} />
          </button>

          <div className="space-y-2">
            <p className="font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-500">
              Select size
            </p>

            {sizeOrder.map((size) => {
              const option = MOBILE_SIZE_OPTIONS[size]
              const isSelected = selectedSize === size

              return (
                <button
                  key={size}
                  type="button"
                  onClick={() => setSelectedSize(size)}
                  className={[
                    'w-full rounded-[18px] border px-3.5 py-2.5 text-left transition-all duration-settle ease-precise focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2',
                    isSelected
                      ? 'border-[rgba(61,59,54,0.45)] bg-white shadow-[0_10px_26px_rgba(61,59,54,0.05)]'
                      : 'border-[rgba(36,34,30,0.08)] bg-white hover:border-[rgba(36,34,30,0.16)]',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-2.5">
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2">
                        <p className="font-sans text-fs-400 font-medium text-ink-700">
                          {option.name}
                        </p>
                        <span className="text-[11px] text-ink-400">
                          {option.dimensions}
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-ink-500">
                        {option.summary}
                      </p>
                    </div>

                    <p className="shrink-0 font-sans text-fs-400 font-medium text-ink-700">
                      {formatUSDWholeFromCents(PRINT_PRICING_CENTS[size])}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>

          <p className="text-[11px] leading-relaxed text-ink-400/90">
            {getShippingPolicyBrief()}
          </p>

          <Button
            variant="primary"
            size="md"
            className="w-full justify-center"
            onClick={() => void handleBuy()}
            loading={isCheckingOut}
          >
            {printCtaLabel}
          </Button>
        </DialogContent>
      </Dialog>

      <RefineSheet
        open={refineOpen}
        onClose={() => setRefineOpen(false)}
        onLayout={handleCompose}
        onCorners={handleShape}
        onSpacing={handleSpace}
        composePreset={state.composePreset}
        cornerStyle={state.cornerStyle}
        spacingStyle={state.spacingStyle}
        disabled={isLandscapeViewOnly || state.elements.length === 0}
      />
    </>
  )
}

export default MobileBoardFlow
